import React from 'react'
import { Edit, useForm } from '@refinedev/antd'
import { useOne } from '@refinedev/core'
import { Form, Select, Input, Typography } from 'antd'
import type { Tables } from '@muaa/shared'

type MentorshipRequest = Tables<'mentorship_requests'>
type Profile = Tables<'profiles'>

const { Text } = Typography

// Full lifecycle vocabulary (a DB CHECK constraint enforces this set). This is
// the admin OVERRIDE screen — staff can force any status, including buckets the
// mobile respond flow never sets (e.g. cancelled), to repair stuck rows.
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'deferred', label: 'Deferred' },
  { value: 'cancelled', label: 'Cancelled' },
]

export function MentorshipRequestEdit() {
  const { formProps, saveButtonProps, query } = useForm<MentorshipRequest>()
  const record = query?.data?.data

  // Resolve the From/To user ids to display names (profiles keyed by user_id).
  // liveMode 'off' is REQUIRED: two useOne calls on the same `profiles` resource
  // would each try to add a postgres_changes callback to the shared
  // realtime:resources/profiles channel, and the second `.on(...)` after
  // `subscribe()` throws ("cannot add postgres_changes callbacks ... after
  // subscribe()"), blanking the page. These are static name lookups — no live
  // updates needed.
  const { query: fromQuery } = useOne<Profile>({
    resource: 'profiles',
    id: record?.from_user_id ?? '',
    meta: { idColumnName: 'user_id' },
    liveMode: 'off',
    queryOptions: { enabled: !!record?.from_user_id },
  })
  const { query: toQuery } = useOne<Profile>({
    resource: 'profiles',
    id: record?.to_user_id ?? '',
    meta: { idColumnName: 'user_id' },
    liveMode: 'off',
    queryOptions: { enabled: !!record?.to_user_id },
  })

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        {/* Display-only context so staff know which request they're overriding. */}
        <Form.Item label="From">
          <Input value={fromQuery.data?.data?.name ?? record?.from_user_id ?? '—'} disabled />
        </Form.Item>

        <Form.Item label="To">
          <Input value={toQuery.data?.data?.name ?? record?.to_user_id ?? '(any)'} disabled />
        </Form.Item>

        <Form.Item label="Message">
          <Input.TextArea value={record?.message ?? '—'} disabled autoSize={{ minRows: 1 }} />
        </Form.Item>

        <Form.Item label="Created">
          <Input value={record?.created_at ?? '—'} disabled />
        </Form.Item>

        {/* The editable field: admin override of the request status. */}
        <Form.Item label="Status" name="status" rules={[{ required: true }]}>
          <Select options={STATUS_OPTIONS} placeholder="Select status" />
        </Form.Item>

        <Text type="secondary">
          Admin override — forcing a status here writes directly to this row, bypassing the
          mentorship-request-respond Edge Function. It does NOT create the mentorship (no
          mentorship_relationships row), does NOT open the Stream chat channel, and does NOT
          notify the mentor or the member. Those three things only happen when the mentor
          accepts the request from their own device. Use this to repair a stuck row, not to
          accept a request on a mentor's behalf.
        </Text>
      </Form>
    </Edit>
  )
}
